import { useEffect, useId, useLayoutEffect, useRef } from 'react'
import type { ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, X } from 'lucide-react'
import { cx } from '@shared/lib/cx'
import {
  backdrop,
  backdropBottom,
  body,
  closeButton,
  descriptionText,
  footerSlot,
  header,
  headerActionsSlot,
  headerCopy,
  headerLead,
  panel,
  panelAudit,
  panelBottom,
  panelCompact,
  panelStandard,
  panelWide,
  statusSlot,
  tabsSlot,
  titleText,
} from './Drawer.css'

export type DrawerSize = 'compact' | 'standard' | 'wide' | 'audit'
export type DrawerVariant = 'detail' | 'financial' | 'form' | 'technical' | 'navigation'
export type DrawerPlacement = 'right' | 'bottom'
export type DrawerCloseReason = 'close-button' | 'escape' | 'backdrop' | 'action'

export type DrawerProps = {
  'data-testid'?: string
  open: boolean
  onClose: (reason?: DrawerCloseReason) => void
  title: ReactNode
  description?: ReactNode
  eyebrow?: ReactNode
  status?: ReactNode
  headerActions?: ReactNode
  size?: DrawerSize
  variant?: DrawerVariant
  placement?: DrawerPlacement
  dismissible?: boolean
  busy?: boolean
  dirty?: boolean
  dirtyMessage?: string
  tabs?: ReactNode
  footer?: ReactNode
  initialFocus?: RefObject<HTMLElement | null>
  finalFocus?: RefObject<HTMLElement | null>
  onBack?: () => void
  backLabel?: string
  children: ReactNode
}

let bodyLockCount = 0
let originalBodyOverflow = ''

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function getFocusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true',
  )
}

function isTopmostDialog(panel: HTMLElement) {
  const dialogs = Array.from(document.querySelectorAll<HTMLElement>('[aria-modal="true"]'))
  return dialogs.at(-1) === panel
}

export function Drawer({
  open,
  onClose,
  title,
  description,
  eyebrow,
  status,
  headerActions,
  size = 'standard',
  variant = 'detail',
  placement = 'right',
  dismissible = true,
  busy = false,
  dirty = false,
  dirtyMessage = 'Descartar as alterações não salvas?',
  tabs,
  footer,
  initialFocus,
  finalFocus,
  onBack,
  backLabel = 'Voltar',
  children,
  ...rest
}: DrawerProps) {
  const titleId = useId()
  const descriptionId = useId()
  const panelRef = useRef<HTMLElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const closeConfigRef = useRef({ busy, dirty, dirtyMessage, dismissible, onClose })

  useEffect(() => {
    closeConfigRef.current = { busy, dirty, dirtyMessage, dismissible, onClose }
  }, [busy, dirty, dirtyMessage, dismissible, onClose])

  useLayoutEffect(() => {
    if (open) {
      bodyRef.current?.scrollTo?.(0, 0)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      return
    }

    const previouslyFocused =
      finalFocus?.current ??
      (document.activeElement instanceof HTMLElement ? document.activeElement : null)

    if (bodyLockCount === 0) {
      originalBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
    bodyLockCount += 1

    const focusTarget = initialFocus?.current ?? titleRef.current ?? panelRef.current
    focusTarget?.focus()

    function handleKeyDown(event: KeyboardEvent) {
      const currentPanel = panelRef.current
      if (!currentPanel || !isTopmostDialog(currentPanel)) {
        return
      }

      if (event.key === 'Escape') {
        const closeConfig = closeConfigRef.current
        if (
          closeConfig.dismissible &&
          !closeConfig.busy &&
          (!closeConfig.dirty || window.confirm(closeConfig.dirtyMessage))
        ) {
          event.preventDefault()
          event.stopPropagation()
          closeConfig.onClose('escape')
        }
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      const focusable = getFocusableElements(currentPanel)
      if (focusable.length === 0) {
        event.preventDefault()
        currentPanel.focus()
        return
      }

      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      const active = document.activeElement

      const focusOutsidePanel = !currentPanel.contains(active)

      if (event.shiftKey && (active === first || focusOutsidePanel)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (active === last || focusOutsidePanel)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      bodyLockCount = Math.max(0, bodyLockCount - 1)
      if (bodyLockCount === 0) {
        document.body.style.overflow = originalBodyOverflow
      }
      previouslyFocused?.focus()
    }
  }, [finalFocus, initialFocus, open])

  if (!open) {
    return null
  }

  function requestClose(reason: DrawerCloseReason) {
    if (busy) {
      return
    }
    if (dirty && !window.confirm(dirtyMessage)) {
      return
    }
    onClose(reason)
  }

  function requestBack() {
    if (busy || !onBack) return
    if (dirty && !window.confirm(dirtyMessage)) return
    onBack()
  }

  return createPortal(
    <div
      className={cx(backdrop, placement === 'bottom' && backdropBottom)}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && dismissible && !busy) {
          requestClose('backdrop')
        }
      }}
    >
      <aside
        ref={panelRef}
        {...rest}
        className={cx(
          panel,
          size === 'compact' && panelCompact,
          size === 'standard' && panelStandard,
          size === 'wide' && panelWide,
          size === 'audit' && panelAudit,
          placement === 'bottom' && panelBottom,
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        aria-busy={busy || undefined}
        data-drawer-size={size}
        data-drawer-variant={variant}
        data-drawer-placement={placement}
        tabIndex={-1}
      >
        <header className={header}>
          <div className={headerLead}>
            {onBack ? (
              <button
                type="button"
                className={closeButton}
                onClick={requestBack}
                aria-label={backLabel}
                disabled={busy}
              >
                <ArrowLeft size={19} aria-hidden="true" />
              </button>
            ) : null}
            <div className={headerCopy}>
              {eyebrow ? <span>{eyebrow}</span> : null}
              <h2 ref={titleRef} className={titleText} id={titleId} tabIndex={-1}>
                {title}
              </h2>
              {description ? (
                <div className={descriptionText} id={descriptionId}>
                  {description}
                </div>
              ) : null}
              {status ? <div className={statusSlot}>{status}</div> : null}
            </div>
          </div>
          <div className={headerActionsSlot}>
            {headerActions}
            <button
              type="button"
              className={closeButton}
              onClick={() => requestClose('close-button')}
              aria-label="Fechar"
              disabled={busy}
            >
              <X size={19} aria-hidden="true" />
            </button>
          </div>
        </header>
        {tabs ? <div className={tabsSlot}>{tabs}</div> : null}
        <div ref={bodyRef} className={body}>
          {children}
        </div>
        {footer ? <footer className={footerSlot}>{footer}</footer> : null}
      </aside>
    </div>,
    document.body,
  )
}
